// client/src/components/canvas/CaptionStylePicker.tsx
// v6.53 — CapCut-style caption preset picker (Dan: "screen pop up like
// capcut for different style options, but in our branding colours"). Opens
// from the Captions hover button on a generated video tile; clicking a
// swatch closes the popup and burns IN PLACE (v6.54): the tile shows a
// "Generating captions" overlay, then its video swaps to the captioned cut.
// Reopen to switch styles (re-burned from the clean master) or turn Off.
//
// Rendered at WORKSPACE level, not inside the tile: React Flow nodes are
// CSS-transformed, and position:fixed inside a transformed ancestor anchors
// to the node instead of the viewport.
import { useEffect, useState } from 'react';
import { useCanvasStore } from '@/pages/canvas/canvas-store';
import { runVideoCaptions, CAPTION_PRESET_SWATCHES } from '@/pages/canvas/video-clone-runner';

export function CaptionStylePicker() {
  const picker = useCanvasStore((s) => s.captionPicker);
  const setCaptionPicker = useCanvasStore((s) => s.setCaptionPicker);
  // v6.54 — current state: which preset (if any) produced this tile's video.
  // Lets the grid highlight the active style and show "Off".
  const [activePreset, setActivePreset] = useState<string | null>(null);
  useEffect(() => {
    setActivePreset(null);
    if (!picker) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/video-clone/${picker.draftId}`);
        if (!res.ok || cancelled) return;
        const d = await res.json();
        const map = (d?.script?.captionsByVideo ?? {}) as Record<string, { preset: string; captionedUrl: string }>;
        const hit = Object.values(map).find((v) => v.captionedUrl === picker.videoUrl);
        if (!cancelled) setActivePreset(hit?.preset ?? null);
      } catch { /* highlight is a nicety */ }
    })();
    return () => { cancelled = true; };
  }, [picker?.imageId, picker?.videoUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!picker) return null;

  return (
    <div
      onClick={() => setCaptionPicker(null)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'rgba(26,26,26,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      data-testid="caption-style-picker"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          border: '1px solid #E5E2DC',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          padding: '20px 22px 22px',
          width: 420,
          maxWidth: '92vw',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A' }}>Caption style</span>
          <button
            onClick={() => setCaptionPicker(null)}
            style={{ border: 'none', background: 'none', color: '#8C8578', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <p style={{ fontSize: 12, color: '#6B6760', margin: '0 0 14px' }}>
          TikTok-style dialog captions, timed to the script — applied to this video. Reopen to switch
          styles or turn them off; the clean original is always kept.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {[{ id: 'off', name: 'Off', swatchText: '∅', swatchStyle: { color: '#8C8578' } }, ...CAPTION_PRESET_SWATCHES].map((p) => (
            <button
              key={p.id}
              data-testid={`caption-preset-${p.id}`}
              onClick={() => {
                setCaptionPicker(null);
                void runVideoCaptions(picker.imageId, p.id);
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#9F2C53'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(159,44,83,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E2DC'; e.currentTarget.style.boxShadow = 'none'; }}
              style={{
                border: (activePreset ?? 'off') === p.id ? '1.5px solid #9F2C53' : '1.5px solid #E5E2DC',
                borderRadius: 12,
                background: '#F7F6F3',
                padding: '14px 8px 10px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                transition: 'border-color 120ms, box-shadow 120ms',
              }}
            >
              {/* Swatch — CSS approximation of the burned style */}
              <span style={{ ...p.swatchStyle, fontSize: 22, lineHeight: '30px', fontWeight: 800, fontFamily: 'Montserrat, Arial Black, sans-serif' }}>
                {p.swatchText}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1A1A' }}>{p.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
